import uuid from "uuid";
import { push } from "connected-react-router";
import { of } from "rxjs";
import { ajax } from "rxjs/ajax";
import { ofType } from "redux-observable";
import { mergeMap, catchError, flatMap } from "rxjs/operators";
import { onJobSafetyAnalysisListRequest } from "features/health-and-safety";
import { HEALTH_SAFETY_JOB_SAFETY_ANALYSIS_PATH } from "features/health-and-safety";
import { ADD_JSA_FORM_SUBMIT } from "../actions/types";
import {
	DBService,
	JOB_SAFETY_ANALYSIS_TABLE,
	replaceDataURLs,
	authHeaders,
	getOrganisationBaseUrl,
} from "frame";
import * as R from "ramda";
import ReactGA from "react-ga";
import { getCurrentTimestamp, isProd } from "utils";
import {
	onAddTasksReset,
	onAddTasksSaveFailed,
	onAddTasksSaveSuccess,
} from "../actions";

const fetchAttachments = () => async () => {
	const db = new DBService();
	var files = await db.getAll(JOB_SAFETY_ANALYSIS_TABLE);
	return files;
};

const addJobSafetyAnalysisEffect = (action$, state$) =>
	action$.pipe(
		ofType(ADD_JSA_FORM_SUBMIT),
		flatMap(fetchAttachments()),
		mergeMap((result) => {
			const formState = R.clone(
				state$.value.addJobSafetyAnalysisFormReducer
			);

			// Normalise attachments
			const attachments = Array.isArray(formState.attachments)
				? replaceDataURLs(formState.attachments, result)
				: [];

			// Normalise tasks and nested steps to always include Hazards & HazardList arrays
			const tasks = Array.isArray(formState.tasks)
				? formState.tasks.map((task, taskIndex) => ({
						...task,
						index: task.index ?? taskIndex,
						steps: Array.isArray(task.steps)
							? task.steps.map((step, stepIndex) => ({
									...step,
									index: step.index ?? stepIndex,
									hazards: Array.isArray(step.hazards)
										? step.hazards
										: [],
									hazardList: Array.isArray(step.hazardList)
										? step.hazardList
										: [],
							  }))
							: [],
				  }))
				: [];

			// Creator info from profile (for CreatorId / CreatorName validation)
			const profile = state$.value.profile || {};
			const creatorId =
				profile.userId || profile.id || profile.staffId || "";
			const creatorName =
				profile.fullName ||
				`${profile.firstName || ""} ${profile.lastName || ""}`.trim();

			// Normalise siteId: send only if it looks like a valid GUID string
			let siteIdToSend = undefined;
			if (formState.siteId && typeof formState.siteId === "string") {
				const trimmed = formState.siteId.trim();
				// Basic GUID format check (36 chars with hyphens)
				const guidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
				if (guidRegex.test(trimmed)) {
					siteIdToSend = trimmed;
				}
			}

			return ajax
				.post(
					`${getOrganisationBaseUrl(
						state$.value
					)}/job-safety-analysis/createorupdate`,
					{
						id: formState.id || uuid.v4(),
						name: formState.name,
						createdAt: formState.createdAt,
						// Only include siteId when it's a valid GUID string; otherwise
						// let the backend default/ignore rather than failing JSON binding.
						...(siteIdToSend ? { siteId: siteIdToSend } : {}),
						creatorId: creatorId,
						creatorName: creatorName,
						tasks: tasks,
						recipients: Array.isArray(formState.recipients)
							? formState.recipients
							: [],
						recipientsOther: Array.isArray(formState.recipientsOther)
							? formState.recipientsOther
							: [],
						sendEmail: Boolean(formState.sendToRecipientsOnSave),
						attachments: attachments,
						latitude: state$.value.geolocation.latitude,
						longitude: state$.value.geolocation.longitude,
						timestamp: getCurrentTimestamp(),
						organisationId:
							state$.value.organisations.currentOrganisation,
					},
					authHeaders()
				)
				.pipe(
					mergeMap((ajaxResponse) => {
						isProd &&
							ReactGA.event({
								category: "Tasks",
								action: "Save Clicked",
								label: "Add Job Safety Analysis",
							});
						return of(
							onJobSafetyAnalysisListRequest(),
							onAddTasksSaveSuccess(),
							onAddTasksReset(),
							push(HEALTH_SAFETY_JOB_SAFETY_ANALYSIS_PATH)
						);
					}),
					catchError((error) =>
						of(
							onAddTasksSaveFailed(
								(error.response && error.response.error) ||
									error.message
							)
						)
					)
				);
		})
	);

export default addJobSafetyAnalysisEffect;
